import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { AdminHeader, Card, Btn, Modal, Input, Textarea, Toggle, StatusBadge, Empty } from "@/components/admin/ui";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Wrench } from "lucide-react";

const EMPTY = { name: "", shortDescription: "", description: "", image: "", icon: "wrench", category: "", startingPrice: "", duration: "", ctaText: "Visit Store", featured: false, order: 0, active: true };

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);

  const load = () => api.get("/admin/services").then(({ data }) => setItems(data.services));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, startingPrice: f.startingPrice === "" ? null : Number(f.startingPrice) };
      if (editId) await api.put(`/admin/services/${editId}`, payload); else await api.post("/admin/services", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this service?")) return; await api.delete(`/admin/services/${id}`); load(); };

  return (
    <div>
      <AdminHeader title="Services" subtitle="In-store services (not purchasable online)" action={<Btn variant="accent" onClick={() => { setF(EMPTY); setEditId(null); setOpen(true); }} data-testid="add-service-btn"><Plus className="h-4 w-4" /> Add Service</Btn>} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <Card key={s.id} className="p-4" data-testid={`admin-service-${s.slug}`}>
            <div className="flex items-start gap-3">
              <span className="h-11 w-11 rounded-lg bg-slate-900 grid place-items-center shrink-0"><Wrench className="h-5 w-5 text-amber-400" /></span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><p className="font-semibold text-slate-900 line-clamp-1">{s.name}</p><StatusBadge status={s.active ? "active" : "inactive"} /></div>
                <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{s.shortDescription}</p>
              </div>
            </div>
            <div className="flex justify-end gap-1 mt-2">
              <button onClick={() => { setF({ ...s, startingPrice: s.startingPrice ?? "" }); setEditId(s.id); setOpen(true); }} className="p-2 text-slate-500 hover:text-amber-600" data-testid={`edit-service-${s.slug}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => del(s.id)} className="p-2 text-slate-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </Card>
        ))}
        {items.length === 0 && <Empty text="No services yet" />}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Service" : "Add Service"}>
        <form onSubmit={save} className="space-y-4">
          <Input label="Service Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} testid="service-name" />
          <Input label="Short Description" value={f.shortDescription} onChange={(e) => setF({ ...f, shortDescription: e.target.value })} testid="service-short" />
          <Textarea label="Detailed Description" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          <Input label="Image URL (optional)" value={f.image || ""} onChange={(e) => setF({ ...f, image: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Category (optional)" value={f.category || ""} onChange={(e) => setF({ ...f, category: e.target.value })} />
            <Input label="Icon key (e.g. wrench, smartphone)" value={f.icon || ""} onChange={(e) => setF({ ...f, icon: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Starting Price ₹ (optional)" type="number" value={f.startingPrice} onChange={(e) => setF({ ...f, startingPrice: e.target.value })} />
            <Input label="Duration (optional)" value={f.duration || ""} onChange={(e) => setF({ ...f, duration: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3 items-center">
            <Input label="CTA Text" value={f.ctaText || ""} onChange={(e) => setF({ ...f, ctaText: e.target.value })} />
            <Input label="Order" type="number" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} />
          </div>
          <div className="flex gap-6">
            <Toggle label="Featured" checked={f.featured} onChange={(v) => setF({ ...f, featured: v })} />
            <Toggle label="Active" checked={f.active} onChange={(v) => setF({ ...f, active: v })} />
          </div>
          <Btn variant="accent" type="submit" className="w-full" data-testid="save-service-btn">Save</Btn>
        </form>
      </Modal>
    </div>
  );
}
